/**
 * Homeowner Hub Analytics — GA4 event tracking for customer portal.
 *
 * Naming convention: `homeowner_<noun>_<verb>`
 * All events fire through the existing GA4 trackEvent helper.
 * Silent-fail: analytics must never break the portal.
 */

import { trackEvent as ga4TrackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

/**
 * First-party persistence: writes a row to `homeowner_activity_events`
 * via the `log-homeowner-activity` edge function. GDPR-safe — only
 * whitelisted payload keys are stored server-side.
 *
 * Silent-fail: must never throw or block the UI.
 */
function persistHomeownerEvent(eventType: string, payload?: Record<string, unknown>) {
  try {
    if (typeof window === "undefined") return;
    // Fire-and-forget; don't await
    void supabase.functions
      .invoke("log-homeowner-activity", {
        body: {
          event_type: eventType,
          event_payload: payload ?? {},
          page_path: window.location.pathname,
          session_id: sessionStorage.getItem("_evo_sid") ?? undefined,
        },
      })
      .catch(() => undefined);
  } catch {
    /* never block */
  }
}

function trackEvent(eventName: string, params?: Record<string, unknown>) {
  ga4TrackEvent(eventName, params);
  if (eventName.startsWith("homeowner_")) {
    persistHomeownerEvent(eventName, params);
  }
}

/* ─── Dashboard & navigation ─── */

export const trackDashboardView = (ctx: {
  hasJobs: boolean;
  activeCount: number;
  stage: "pre_deal" | "active" | "completed";
}) => trackEvent("homeowner_dashboard_view", ctx);

export const trackProjectView = (jobId: string, status: string) =>
  trackEvent("homeowner_project_view", { job_id: jobId, status });

/* ─── Property ─── */

export const trackPropertyCardOpen = (hasEpc: boolean) =>
  trackEvent("homeowner_property_card_open", { has_epc: hasEpc });

export const trackPropertyLookup = (postcode: string) =>
  trackEvent("homeowner_property_lookup", { postcode_prefix: postcode.slice(0, 4) });

/* ─── Documents ─── */

export const trackDocumentView = (docType: string) =>
  trackEvent("homeowner_document_view", { doc_type: docType });

export const trackDocumentDownload = (docType: string) =>
  trackEvent("homeowner_document_download", { doc_type: docType });

/* ─── Media / uploads ─── */

export const trackMediaUploadStart = (source: string) =>
  trackEvent("homeowner_media_upload_start", { source });

export const trackMediaUploadComplete = (source: string, count: number) =>
  trackEvent("homeowner_media_upload_complete", { source, file_count: count });

/* ─── Messages ─── */

export const trackMessageOpen = () =>
  trackEvent("homeowner_message_open");

export const trackMessageSend = () =>
  trackEvent("homeowner_message_send");

/* ─── AI / Insights ─── */

export const trackInsightView = (insightType: string) =>
  trackEvent("homeowner_ai_insight_view", { insight_type: insightType });

export const trackInsightAction = (insightType: string) =>
  trackEvent("homeowner_ai_insight_action", { insight_type: insightType });

export const trackNextActionClick = (actionType: string) =>
  trackEvent("homeowner_next_action_click", { action_type: actionType });

/* ─── Pre-deal ─── */

export const trackConsultationCtaClick = (variant: "hero" | "pre_deal" | "inline") =>
  trackEvent("homeowner_consultation_cta_click", { variant });

export const trackPreparationCheckComplete = (item: string) =>
  trackEvent("homeowner_preparation_check", { item });

export const trackRecommendationView = (service: string) =>
  trackEvent("homeowner_recommendation_view", { service });

/* ─── Project detail tabs ─── */

export const trackProjectTabChange = (tab: string, jobId: string) =>
  trackEvent("homeowner_project_tab", { tab, job_id: jobId });

/* ─── Aftercare ─── */

export const trackAftercareOpen = (jobId: string) =>
  trackEvent("homeowner_aftercare_open", { job_id: jobId });

export const trackSnaggingReport = (jobId: string) =>
  trackEvent("homeowner_snagging_report", { job_id: jobId });

/* ─── Funnel stage helper ─── */

export type HomeownerStage =
  | "signed_up_no_project"
  | "has_interests_no_booking"
  | "booked_consultation"
  | "quoted"
  | "active_project"
  | "completed_project"
  | "aftercare_active";

export function deriveHomeownerStage(ctx: {
  hasJobs: boolean;
  hasInterests: boolean;
  hasEnquiry: boolean;
  statuses: string[];
}): HomeownerStage {
  const { hasJobs, hasInterests, hasEnquiry, statuses } = ctx;

  if (!hasJobs && !hasEnquiry && !hasInterests) return "signed_up_no_project";
  if (!hasJobs && hasInterests) return "has_interests_no_booking";
  if (!hasJobs && hasEnquiry) return "booked_consultation";

  const activeStatuses = new Set(["scheduled", "booked_in", "in_progress", "ready_for_signoff"]);
  const quotedStatuses = new Set(["quote_pack_draft", "quote_requests_sent", "installer_shortlisted", "survey_scheduled"]);
  const completedStatuses = new Set(["approved_for_payment", "paid", "completed"]);

  if (statuses.some(s => s === "aftercare_open")) return "aftercare_active";
  if (statuses.some(s => completedStatuses.has(s))) return "completed_project";
  if (statuses.some(s => activeStatuses.has(s))) return "active_project";
  if (statuses.some(s => quotedStatuses.has(s))) return "quoted";

  return "booked_consultation";
}

/** Fire a funnel-stage identification event once per session per stage */
const _firedStages = new Set<string>();
export function trackFunnelStage(stage: HomeownerStage) {
  if (_firedStages.has(stage)) return;
  _firedStages.add(stage);
  trackEvent("homeowner_funnel_stage", { stage });
}
